export const MUULTI_GREEN = "#183f43";
export const PANEL_WIDTH = 6.8;
export const PANEL_HEIGHT = 6.5;
export const PANEL_BOTTOM = 1;
export const PANEL_HTML_WIDTH = 480;
export const PANEL_FRAME_PADDING = 0.18;
export const PANEL_WORLD_PER_PIXEL = (PANEL_WIDTH - PANEL_FRAME_PADDING * 2) / PANEL_HTML_WIDTH;
export const PANEL_HTML_DISTANCE_FACTOR = PANEL_WORLD_PER_PIXEL * 400;
export const HEADER_BAND_HEIGHT = 2.4;
export const ROOM_HEIGHT = 16;
export const STREET_HALF_WIDTH = 8;

export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function getPanelFrameHeight(pixelHeight) {
    const height = Number(pixelHeight);
    return Number.isFinite(height) && height > 0
        ? Math.max(PANEL_HEIGHT, height * PANEL_WORLD_PER_PIXEL + PANEL_FRAME_PADDING * 2)
        : PANEL_HEIGHT;
}

export function getRoomHeight(panelHeights = []) {
    const tallest = Math.max(PANEL_HEIGHT, ...panelHeights.filter(Number.isFinite));
    return Math.max(ROOM_HEIGHT, PANEL_BOTTOM + tallest + HEADER_BAND_HEIGHT + 1.2);
}

// One slot is reserved for a door. All panels stay on a single accessible row.
export function buildRoomLayout(count = 0, panelHeights = []) {
    const total = Math.max(0, count);
    const slots = Math.max(2, Math.ceil((total + 1) / 4));
    const side = Math.max(22, slots * 8.5 + 4);
    const half = side / 2;
    // Balance panels across all four walls, leaving the front wall room for its door.
    const wallOrder = [0, 1, 3, 2];
    const counts = [0, 0, 0, 0];
    for (let index = 0; index < total; index += 1) counts[wallOrder[index % 4]] += 1;
    const walls = [[], [], [], []];
    let door;
    for (let wall = 0; wall < 4; wall += 1) {
        const cellCount = counts[wall] + (wall === 2 ? 1 : 0);
        const spacing = (side - 4) / Math.max(1, cellCount);
        const doorSlot = Math.floor(cellCount / 2);
        for (let slot = 0; slot < cellCount; slot += 1) {
            const along = -half + 2 + spacing * (slot + 0.5);
            const positions = [
                [along, 3.7, -half + 0.24],
                [half - 0.24, 3.7, along],
                [-along, 3.7, half - 0.24],
                [-half + 0.24, 3.7, -along],
            ];
            const cell = { position: positions[wall], rotation: [0, -wall * Math.PI / 2, 0] };
            if (wall === 2 && slot === doorSlot) door = cell;
            else walls[wall].push(cell);
        }
    }
    const panels = [];
    for (let index = 0; index < total; index += 1) {
        const cell = walls[wallOrder[index % 4]].shift();
        const height = Number.isFinite(panelHeights[index]) ? Math.max(PANEL_HEIGHT, panelHeights[index]) : PANEL_HEIGHT;
        panels.push({ ...cell, position: [cell.position[0], PANEL_BOTTOM + height / 2, cell.position[2]], height });
    }
    return {
        side, half, height: getRoomHeight(panelHeights), door, panels,
        bounds: { minX: -half + 0.9, maxX: half - 0.9, minZ: -half + 0.9, maxZ: half - 0.9 },
        entrance: { x: door.position[0], z: half - 4.5, yaw: 0, pitch: 0 },
    };
}

export function buildStreetLayout(users) {
    const entries = [], streets = [], blocks = [], frontiers = [];
    const columns = 4;
    const sidewalk = 6;
    const courtyard = 12;
    const streetGap = STREET_HALF_WIDTH * 2 + sidewalk * 2;
    const frontage = STREET_HALF_WIDTH + sidewalk;
    const startX = -buildRoomLayout(0).half;
    const facings = [Math.PI, Math.PI / 2, -Math.PI / 2, 0];
    const crossStreets = [];
    let rowTop = frontage, rowBottom = 0, maxRight = -startX;
    for (let start = 0; start < users.length; start += columns) {
        const row = users.slice(start, start + columns).map(user => ({ user, room: buildRoomLayout(user.componentCount) }));
        const rowDepth = Math.max(...row.map(({ room }) => room.side));
        rowBottom = rowTop + rowDepth;
        crossStreets.push(rowTop - frontage);
        streets.push({ position: [startX - frontage, 0, (rowTop + rowBottom) / 2], width: STREET_HALF_WIDTH * 2, depth: rowDepth + sidewalk * 2 });
        let left = startX, blockLeft = startX, blockDepth = 0;
        for (const [column, { user, room }] of row.entries()) {
            if (column % 2 === 0) { blockLeft = left; blockDepth = 0; }
            const x = left + room.half, z = rowTop + room.half;
            const yaw = facings[column];
            const right = left + room.side;
            const doorX = room.door.position[0], doorZ = room.half + STREET_HALF_WIDTH;
            entries.push({
                user, room, position: [x, 0, z], rotation: [0, yaw, 0],
                entranceX: x + doorX * Math.cos(yaw) + doorZ * Math.sin(yaw),
                entranceZ: z - doorX * Math.sin(yaw) + doorZ * Math.cos(yaw),
                entranceYaw: yaw,
            });
            maxRight = Math.max(maxRight, right);
            blockDepth = Math.max(blockDepth, room.side);
            if (column % 2 === 1 || column === row.length - 1) {
                blocks.push({ position: [(blockLeft + right) / 2, 0, rowTop + blockDepth / 2],
                    width: right - blockLeft + sidewalk * 2, depth: blockDepth + sidewalk * 2 });
                streets.push({ position: [right + frontage, 0, (rowTop + rowBottom) / 2],
                    width: STREET_HALF_WIDTH * 2, depth: rowDepth + sidewalk * 2 });
            }
            left = right + (column % 2 === 0 ? courtyard : streetGap);
        }
        // Rows grow outwards. A later page never changes existing building positions.
        if (row.length < columns) frontiers.push({
            minX: left, maxX: Math.max(maxRight + streetGap, left + 22),
            minZ: rowTop - frontage, maxZ: rowBottom + frontage,
        });
        rowTop = rowBottom + streetGap;
    }
    const minX = startX - streetGap, maxX = maxRight + streetGap;
    const minZ = -frontage, maxZ = rowBottom + streetGap;
    const width = maxX - minX, depth = maxZ - minZ;
    const centerX = (minX + maxX) / 2, centerZ = (minZ + maxZ) / 2;
    crossStreets.push(entries.length ? rowBottom + frontage : 0);
    crossStreets.forEach(z => streets.push({ position: [centerX, 0, z], width, depth: STREET_HALF_WIDTH * 2 }));
    if (entries.length) frontiers.push({
        minX: startX - frontage, maxX: maxRight + frontage,
        minZ: rowBottom + frontage, maxZ: rowBottom + frontage,
    });
    const obstacles = entries.map(entry => ({
        minX: entry.position[0] - entry.room.half - 0.6, maxX: entry.position[0] + entry.room.half + 0.6,
        minZ: entry.position[2] - entry.room.half - 0.6, maxZ: entry.position[2] + entry.room.half + 0.6,
    }));
    return {
        entries, streets, blocks, frontiers, width, depth, centerX, centerZ,
        bounds: { minX: minX + 1, maxX: maxX - 1, minZ: minZ + 1, maxZ: maxZ - 1, obstacles },
    };
}

export function isNearStreetFrontier(frontiers, position, distance) {
    return frontiers.some(frontier => {
        const dx = Math.max(frontier.minX - position.x, 0, position.x - frontier.maxX);
        const dz = Math.max(frontier.minZ - position.z, 0, position.z - frontier.maxZ);
        return dx * dx + dz * dz <= distance * distance;
    });
}

// Leave room for the player at walls, while allowing movement along them.
export function constrainScenePosition(position, bounds) {
    const next = { x: clamp(position.x, bounds.minX, bounds.maxX), z: clamp(position.z, bounds.minZ, bounds.maxZ) };
    for (const wall of bounds.obstacles || []) {
        if (next.x <= wall.minX || next.x >= wall.maxX || next.z <= wall.minZ || next.z >= wall.maxZ) continue;
        const distances = [next.x - wall.minX, wall.maxX - next.x, next.z - wall.minZ, wall.maxZ - next.z];
        const edge = distances.indexOf(Math.min(...distances));
        if (edge === 0) next.x = wall.minX;
        else if (edge === 1) next.x = wall.maxX;
        else if (edge === 2) next.z = wall.minZ;
        else next.z = wall.maxZ;
    }
    return next;
}

export function getNearbyIndices(items, position, distance, limit, getPosition = item => item.position) {
    const maxDistance = distance * distance;
    return items.map((item, index) => {
        const point = getPosition(item);
        const radius = item.radius || 0;
        const dx = Math.max(0, Math.abs(position.x - point[0]) - radius);
        const dz = Math.max(0, Math.abs(position.z - point[2]) - radius);
        return { index, distance: dx * dx + dz * dz };
    }).filter(item => item.distance <= maxDistance)
        .sort((a, b) => a.distance - b.distance || a.index - b.index)
        .slice(0, limit).map(item => item.index);
}

export function movementStep(axisX, axisY, yaw, dt, speed = 6) {
    const scale = speed * Math.min(Math.max(0, dt), 0.05) / Math.max(1, Math.hypot(axisX, axisY));
    return {
        x: (Math.cos(yaw) * axisX + Math.sin(yaw) * axisY) * scale,
        z: (-Math.sin(yaw) * axisX + Math.cos(yaw) * axisY) * scale,
    };
}

function imageUrl(value) {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    const match = trimmed.match(/^url\((['"]?)(.*?)\1\)$/i);
    return match ? match[2] : trimmed;
}

// Respect the selected mode so old image/gradient fields do not override a color.
export function resolveSurface(background = {}, fallback = MUULTI_GREEN) {
    const bg = background || {};
    const type = String(bg.type || "").trim().toLowerCase();
    const image = [bg.imageUrl, bg.url, bg.image, bg.src].map(imageUrl).find(Boolean) || "";
    const cssImage = String(bg.backgroundImage || bg.background || "").match(/url\((['"]?)(.*?)\1\)/i)?.[2] || "";
    const usesImage = type === "image" || type === "cover" || (!type && (image || cssImage));
    const colors = (type === "gradient" || !type) && Array.isArray(bg.colors) ? bg.colors : [];
    return {
        imageUrl: usesImage ? image || cssImage : "",
        colors, direction: bg.direction || "to right",
        color: type === "none" ? fallback : (usesImage ? bg.fallback || bg.backgroundColor || bg.color : bg.color) || bg.backgroundColor || colors[0] || fallback,
        sizing: String(bg.sizing || bg.backgroundSize || "cover").trim().toLowerCase(),
        position: String(bg.position || bg.backgroundPosition || "center").trim().toLowerCase(),
        repeatMode: String(bg.repeatMode || bg.backgroundRepeat || "no-repeat").trim().toLowerCase(),
        fontColor: bg.fontColor || "#ffffff",
    };
}

export function summarizeStreetUser(snapshot) {
    const data = snapshot.data() || {};
    const componentCount = Array.isArray(data.components) ? data.components.filter(Boolean).length : 0;
    if (typeof data.muultiName !== "string" || !data.muultiName.trim() || componentCount === 0) return null;
    return {
        id: snapshot.id, publicKey: data.publicKey || snapshot.id, muultiName: data.muultiName,
        pageTitle: data.pageTitle || data.muultiName, pageDescription: data.pageDescription || "",
        profilePicUrl: data.profilePicUrl || "", background: data.background || {},
        headerBackground: data.headerBackground || {}, alignment: ["left", "center", "right"].includes(data.alignment) ? data.alignment : "left", componentCount,
        updatedMs: typeof data.updatedAt?.toMillis === "function" ? data.updatedAt.toMillis() : Number(data.updatedAt) || 0,
    };
}

export function mergeStreetUsers(users, snapshots) {
    const byIdentity = new Map(users.map(user => [user.publicKey, user]));
    snapshots.forEach(snapshot => {
        const candidate = summarizeStreetUser(snapshot);
        if (!candidate) return;
        const existing = byIdentity.get(candidate.publicKey);
        const deterministic = candidate.id === candidate.publicKey;
        const existingDeterministic = existing?.id === existing?.publicKey;
        if (!existing || (deterministic && !existingDeterministic) ||
            (deterministic === existingDeterministic && candidate.updatedMs > existing.updatedMs)) {
            byIdentity.set(candidate.publicKey, candidate);
        }
    });
    // Preserve positions when fetching the next ordered directory batch.
    return Array.from(byIdentity.values());
}
